<template>
  <div class="ren-decoration-items">
    <div class="ren-decoration-field-group">
      <label>自动播放</label>
      <RrSwitch :checked="autoplay" :disabled="readonly" @update:checked="(val) => patch({ autoplay: Boolean(val) })" />
    </div>
    <div class="ren-decoration-field-group">
      <label>切换间隔（毫秒）</label>
      <RrInputNumber
        :value="interval"
        :min="1000"
        :max="60000"
        :disabled="readonly"
        @update:value="onIntervalChange"
      />
    </div>
    <div class="ren-decoration-field-group">
      <label>显示指示器</label>
      <RrSwitch :checked="indicator" :disabled="readonly" @update:checked="(val) => patch({ indicator: Boolean(val) })" />
    </div>
    <div class="ren-decoration-banner-items-editor">
    <div class="ren-decoration-banner-items-editor__head">
      <strong>轮播列表（{{ items.length }} 项）</strong>
      <RrButton type="primary" size="small" :disabled="readonly || items.length >= 12" @click="addItem">
        添加项
      </RrButton>
    </div>

    <article v-for="(item, index) in items" :key="index" class="ren-decoration-banner-item-card">
      <header class="banner-item-card__head">
        <span>第 {{ index + 1 }} 项</span>
        <div class="banner-item-card__actions">
          <RrButton
            size="small"
            type="default"
            :disabled="readonly || index === 0"
            title="上移"
            aria-label="上移"
            @click="moveItem(index, -1)">
            <ArrowUp :size="12" />
          </RrButton>
          <RrButton
            size="small"
            type="default"
            :disabled="readonly || index === items.length - 1"
            title="下移"
            aria-label="下移"
            @click="moveItem(index, 1)">
            <ArrowDown :size="12" />
          </RrButton>
          <RrButton
            size="small"
            type="primary" danger
            :disabled="readonly"
            title="删除"
            aria-label="删除"
            @click="removeItem(index)">
            <Trash2 :size="12" />
          </RrButton>
        </div>
      </header>
      <div class="banner-item-card__body">
        <WidgetImageUpload
          :model-value="itemImage(item)"
          :disabled="readonly"
          @update:model-value="(url) => setItem(index, 'imageUrl', url)"
        />
        <div class="banner-item-card__fields">
          <RrInput
            placeholder="请输入标题"
            :disabled="readonly"
            :value="String(item.title ?? item.label ?? '')"
            @update:value="(val) => setItem(index, 'title', String(val ?? ''))"
          />
          <RrInput
            placeholder="请输入副标题（选填）"
            :disabled="readonly"
            :value="String(item.subtitle ?? '')"
            @update:value="(val) => setItem(index, 'subtitle', String(val ?? ''))"
          />
          <div class="banner-item-card__link">
            <span>{{ itemLink(item) || '未配置跳转' }}</span>
            <RrButton size="small" type="default" :disabled="readonly" @click="emit('pick-item-link', index)">
              选择页面
            </RrButton>
          </div>
        </div>
      </div>
    </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowDown, ArrowUp, Trash2 } from '@lucide/vue'
import { RrButton, RrInput, RrInputNumber, RrSwitch } from '@xjlv2/ui'
import { recordList } from '../../../previews/preview-helpers'
import { mergeWidgetProps, type WidgetSettingsEmits, type WidgetSettingsProps } from '../../types'
import WidgetImageUpload from '../../WidgetImageUpload.vue'

const props = withDefaults(defineProps<WidgetSettingsProps>(), {
  readonly: false
})

const emit = defineEmits<WidgetSettingsEmits>()

const items = computed(() => recordList(props.component.props?.items))
const autoplay = computed(() => Boolean(props.component.props?.autoplay ?? true))
const interval = computed(() => {
  const value = Number(props.component.props?.interval)
  return Number.isFinite(value) && value > 0 ? value : 5000
})
const indicator = computed(() => Boolean(props.component.props?.indicator ?? true))

function patch(partial: Record<string, unknown>) {
  emit('update:props', mergeWidgetProps(props.component, partial))
}

function onIntervalChange(value: number | null) {
  patch({ interval: value == null ? 5000 : Math.max(1000, Math.min(60000, Math.round(value))) })
}

function itemImage(item: Record<string, unknown>) {
  return String(item.imageUrl || item.image || '')
}

function itemLink(item: Record<string, unknown>) {
  return String(item.link || '')
}

function emitItems(next: Record<string, unknown>[]) {
  patch({ items: next })
}

function setItem(index: number, key: string, value: unknown) {
  const next = items.value.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item))
  emitItems(next)
}

function addItem() {
  if (items.value.length >= 12) return
  emitItems([
    ...items.value,
    {
      title: `轮播图 ${items.value.length + 1}`,
      subtitle: '',
      imageUrl: '',
      link: ''
    }
  ])
}

function removeItem(index: number) {
  emitItems(items.value.filter((_, itemIndex) => itemIndex !== index))
}

function moveItem(index: number, offset: -1 | 1) {
  const target = index + offset
  if (target < 0 || target >= items.value.length) return
  const next = [...items.value]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  emitItems(next)
}
</script>

<style scoped>
.banner-item-card__body {
  grid-template-columns: auto minmax(0, 1fr);
}
</style>
